/** 数学与格式化工具（纯函数，无 DOM 依赖）。 */

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

export function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

export function nearlyEqual(a, b, epsilon = 1e-9) {
  return Math.abs(a - b) <= epsilon * Math.max(1, Math.abs(a), Math.abs(b));
}

/** 相对误差，分母带下限，避免参考值为 0 时爆炸。 */
export function relativeError(value, reference, floor = 1e-9) {
  return Math.abs(value - reference) / Math.max(Math.abs(reference), floor);
}

export function signedRelativeDelta(value, reference, floor = 1e-9) {
  return (value - reference) / Math.max(Math.abs(reference), floor);
}

export function round(value, digits = 3) {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/** 把浮点数格式化为教学中常用的有效形式（去掉多余的 0）。 */
export function formatNumber(value, digits = 2) {
  if (!Number.isFinite(value)) return '—';
  if (value !== 0 && (Math.abs(value) >= 100000 || Math.abs(value) < 0.001)) {
    return value.toExponential(Math.min(digits, 3)).replace('e+', '×10^').replace('e-', '×10^-');
  }
  return Number(value.toFixed(digits)).toString();
}

export function formatSigned(value, digits = 2) {
  const text = formatNumber(value, digits);
  return value > 0 ? `+${text}` : text;
}

/** 生成可复现的伪随机序列（教学 Agent 出题用）。 */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function random() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** 算术—几何平均，用于单摆大角度精确周期。 */
export function arithmeticGeometricMean(a, b, iterations = 32) {
  let x = a;
  let y = b;
  for (let i = 0; i < iterations; i += 1) {
    const nextX = (x + y) / 2;
    const nextY = Math.sqrt(x * y);
    x = nextX;
    y = nextY;
  }
  return x;
}

/** 四阶龙格—库塔积分，输入 y 数组与导数函数。 */
export function rk4(y, dt, derivative) {
  const k1 = derivative(y);
  const y2 = y.map((v, i) => v + (dt / 2) * k1[i]);
  const k2 = derivative(y2);
  const y3 = y.map((v, i) => v + (dt / 2) * k2[i]);
  const k3 = derivative(y3);
  const y4 = y.map((v, i) => v + dt * k3[i]);
  const k4 = derivative(y4);
  return y.map((v, i) => v + (dt / 6) * (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i]));
}

export function allFinite(values) {
  return values.every(isFiniteNumber);
}
